import type { CompanyLegalForm } from "@component/SmeLoan/LeadForm/types";
import type { AbstractStepProps, AbstractStepState } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import {
	faBuilding as fasBuilding,
	faCalendarDays as fasCalendarDays,
	faCalendarCheck as fasCalendarCheck,
	faArrowRight as fasArrowRight,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, FormNumber, FormSelect } from "bm-ui-kit";
import type { CommonOptions, FormSelectOptions } from "bm-ui-kit";
import dayjs from "dayjs";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import type { CompanySector } from "../types";

export const COMPANY_LEGAL_FORM_OPTIONS: CommonOptions<CompanyLegalForm> = [
	{ label: "Kft.", value: "KFT" },
	{ label: "Bt.", value: "BT" },
	{ label: "Bt.(KATA)", value: "BT_KATA" },
	{ label: "Zrt.", value: "ZRT" },
	{ label: "Nyrt.", value: "NYRT" },
	{ label: "Kkt.", value: "KKT" },
	{ label: "Egyéni cég", value: "KFC" },
	{ label: "Egyéb vállalkozás", value: "OTHER" },
	{ label: "Szövetkezet", value: "COOPERATIVE" },
	{ label: "Egyéni vállalkozó", value: "SELF_EMPLOYED" },
	{ label: "Egyéni vállalkozó (KATA)", value: "SELF_EMPLOYED_KATA" },
	{ label: "Őstermelő", value: "PRIMARY_PRODUCER" },
	{ label: "Családi gazdálkodó", value: "FAMILY_FARM" },
];

export const COMPANY_SECTORS_OPTIONS: FormSelectOptions<CompanySector> = [
	{
		label: "Turisztika",
		value: "TURISZTIKA",
	},
	{ label: "Agrár", value: "AGRAR" },
	{
		label: "Egyéb",
		value: "EGYEB",
	},
];

export const NUMBER_OF_CLOSED_YEARS_OPTIONS = [
	{ label: "Nincs lezárt év", value: "0" },
	{ label: "1 év", value: "1" },
	{ label: "2 vagy több év", value: "2" },
];

export interface Step2Fields {
	companyLegalForm: CompanyLegalForm;
	companyFoundYear: number;
	companyMainActivity: string;
	companySector: CompanySector;
	numberOfClosedYears: string;
}

interface State extends AbstractStepState {
	numberOfClosedYearsOptions: CommonOptions;
}

export class Step2 extends AbstractStep<Step2Fields, AbstractStepProps, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			numberOfClosedYearsOptions: NUMBER_OF_CLOSED_YEARS_OPTIONS,
		};
	}

	protected renderContent(_values: Step2Fields, form: FormApi<Step2Fields>): ReactNode {
		const { numberOfClosedYearsOptions } = this.state;

		return (
			<>
				<Card.Body>
					<FormSelect
						name="companyLegalForm"
						label="Társasági forma:"
						prepend={<FontAwesomeIcon icon={fasBuilding} />}
						placeholder="Válaszd ki a társasági formát"
						options={COMPANY_LEGAL_FORM_OPTIONS}
						required
					/>
					<FormNumber
						name="companyFoundYear"
						label="Cég alapításának éve:"
						prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
						placeholder="Add meg az alapítás évét"
						onChange={(value) => this.onCompanyFoundYearChange(value, form)}
						validate={this.validCompanyFoundYear}
						required
					/>
					<FormSelect
						name="companySector"
						label="Cég tevékenysége (szektor):"
						placeholder="Válassz ki egy lehetőséget!"
						prepend={<FontAwesomeIcon icon={fasCalendarCheck} />}
						options={COMPANY_SECTORS_OPTIONS}
						required
					/>
					<FormSelect
						name="numberOfClosedYears"
						label="Lezárt pénzügyi (teljes) évek száma:"
						placeholder="Válassz ki egy lehetőséget!"
						prepend={<FontAwesomeIcon icon={fasCalendarCheck} />}
						options={numberOfClosedYearsOptions}
						disabled={numberOfClosedYearsOptions.length === 1}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<Button
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
					>
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	private onCompanyFoundYearChange(companyFoundYear: number, form: FormApi<Step2Fields>): void {
		const age = parseInt(dayjs().format("YYYY"), 10) - companyFoundYear - 1;
		const numberOfClosedYears = form.getFieldState("numberOfClosedYears").value;
		let numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS;

		if (age > 2) {
			numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS.filter((item) => item.value === "2");
		} else if (age === 2) {
			numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS.filter(
				(item) => item.value === "1" || item.value === "2",
			);
		} else if (age === 1) {
			numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS.filter(
				(item) => item.value === "1" || item.value === "3",
			);
		} else if (dayjs(companyFoundYear).year() === dayjs().year() - 1) {
			numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS.filter(
				(item) => item.value === "0" || item.value === "3",
			);
		} else {
			numberOfClosedYearsOptions = NUMBER_OF_CLOSED_YEARS_OPTIONS.filter((item) => item.value === "0");
		}

		if (numberOfClosedYearsOptions.length === 1) {
			form.change("numberOfClosedYears", numberOfClosedYearsOptions[0].value);
		} else if (!numberOfClosedYearsOptions.map((item) => item.value).includes(numberOfClosedYears)) {
			form.change("numberOfClosedYears", undefined);
		}

		this.setState({
			numberOfClosedYearsOptions,
		});
	}

	private validCompanyFoundYear(companyFoundYear: number): string {
		if (!companyFoundYear) {
			return "Kérjük add meg az alapítás évét!";
		}

		if (companyFoundYear < 1800) {
			return "A megadható legalacsonyabb érték 1800!";
		}

		if (companyFoundYear > dayjs().year()) {
			return "A cég alapítási éve nem lehet jövőbeli.";
		}
	}

	protected getComponentName(): string {
		return "Step2";
	}
}
